import { useCallback, useEffect, useRef, useState } from 'react';

import api from '~/services/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEBOUNCE_MS = 400;

const GENERIC_ERROR =
  'Não foi possível iniciar o login pela conta Microsoft. Tente novamente ou contate o administrador.';

const normalize = value => (value || '').trim().toLowerCase();

/**
 * Descoberta de SSO (Entra ID) pelo e-mail digitado no login.
 *
 * - onEmailChange(value): agenda a descoberta (debounce).
 * - onEmailBlur(value):   descobre imediatamente.
 * - start(email):         gera o state no backend e redireciona para a Microsoft.
 */
const useEntraIdSso = () => {
  const [required, setRequired] = useState(false);
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const timer = useRef(null);
  const lastChecked = useRef('');
  const requestId = useRef(0);

  const discover = useCallback(async email => {
    requestId.current += 1;
    const id = requestId.current;

    lastChecked.current = email;
    setChecking(true);

    try {
      const { data } = await api.post('auth/entraid/discover', { email });

      if (id === requestId.current) {
        setRequired(!!data?.entraid_required);
      }
    } catch (err) {
      if (id === requestId.current) {
        setRequired(false);
      }
    } finally {
      if (id === requestId.current) {
        setChecking(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    requestId.current += 1;
    lastChecked.current = '';
    setRequired(false);
    setChecking(false);
  }, []);

  const onEmailChange = useCallback(
    value => {
      clearTimeout(timer.current);
      setError('');

      const email = normalize(value);

      if (!EMAIL_RE.test(email)) {
        reset();
        return;
      }

      if (email === lastChecked.current) {
        return;
      }

      timer.current = setTimeout(() => discover(email), DEBOUNCE_MS);
    },
    [discover, reset]
  );

  const onEmailBlur = useCallback(
    value => {
      clearTimeout(timer.current);

      const email = normalize(value);

      if (!EMAIL_RE.test(email)) {
        reset();
        return;
      }

      if (email !== lastChecked.current) {
        discover(email);
      }
    },
    [discover, reset]
  );

  const start = useCallback(async value => {
    const email = normalize(value);

    setStarting(true);
    setError('');

    try {
      const { data } = await api.post('auth/entraid/start', { email });

      if (data?.entraid_required && data?.authorize_url) {
        window.location.href = data.authorize_url;
        return;
      }

      // SSO deixou de estar disponível: volta para o login por senha.
      setRequired(false);
      setStarting(false);
      setError(
        'Login pela conta Microsoft indisponível para este e-mail. Entre com sua senha.'
      );
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;

      setStarting(false);
      setError(status !== 401 && message ? message : GENERIC_ERROR);
    }
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return {
    required,
    checking,
    starting,
    error,
    onEmailChange,
    onEmailBlur,
    start,
  };
};

export default useEntraIdSso;
